<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      CSV Upload Batches
    </template>
    <template #body>
      <div class="form-group row">
        <div class="form-group mr-2 mb-0 col-12 d-flex">
          <router-link :to="{ name: 'admin_unos_finalized_offers_csv_batch_new' }"
            class="btn btn-primary btn-sm ml-auto">
            Add New Batch
          </router-link>
        </div>
      </div>

      <PortalTable
        table-id="finalized_offers_csv_batches_index"
        ref="finalized_offers_csv_batches_index"
        :table-data="records"
        :total-records="totalRecords"
        :page="pageNumber"
        :page-count="pageCount"
        :loading="isSavableStateProcessing"
        :filter-values="filterValues"
        :add-filter-to-route="true"
        filter-display="row"
        empty="No batches uploaded yet."
        @on-page="handlePageChange"
        @on-sort="handleSortChange"
        @on-filter-change="onFilterChange">
        <template #columns>
          <Column field="transplant_center.code" header="Transplant Center" sortable filter-field="transplant_center_id" :show-filter-menu="false" :filter-menu-style="{ width: '15rem' }" style="min-width: 150px;">
            <template #body="slotProps">
              {{ slotProps.data.transplant_center.code }}
            </template>
            <template #filter>
              <MultiSelectFilter
                ref="transplant_center_id"
                filter-field="transplant_center_id"
                :options="txcStore.transplantCenters"
                @change="onChange" />
            </template>
          </Column>
          <Column field="batch_date.display" header="Batch Date" sort-field="batch_date" sortable filter-field="batch_date" :show-filter-menu="false" style="min-width: 160px;">
            <template #body="slotProps">
              {{ slotProps.data.batch_date.display }}
            </template>
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <VueDatePicker
                v-model="filterModel.value"
                model-type="yyyy-MM-dd"
                :enable-time-picker="false"
                :auto-apply="true"
                teleport="body"
                placeholder="Filter by date"
                @update:model-value="filterCallback()" />
            </template>
          </Column>
          <Column field="batchStatusDisplayText" header="Status" sort-field="status" sortable filter-field="status" :show-filter-menu="false" :filter-menu-style="{ width: '15rem' }" style="min-width: 150px;">
            <template #body="slotProps">
              <span class="status-badge" :class="slotProps.data.batchStatusBadgeClass">
                {{ slotProps.data.batchStatusDisplayText }}
              </span>
            </template>
            <template #filter>
              <MultiSelectFilter
                ref="status"
                filter-field="status"
                option-value="code"
                class="form-control form-control-select"
                option-label="name"
                :options="statusOptions"
                @change="onChange" />
            </template>
          </Column>
          <Column field="created_at.display" header="Created At" sort-field="created_at" sortable filter-field="created_at" :show-filter-menu="false" style="min-width: 200px;">
            <template #body="slotProps">
              {{ slotProps.data.created_at.display }}
            </template>
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <VueDatePicker
                v-model="filterModel.value"
                model-type="yyyy-MM-dd"
                :enable-time-picker="false"
                :auto-apply="true"
                teleport="body"
                placeholder="Filter by date"
                @update:model-value="filterCallback()" />
            </template>
          </Column>
          <Column field="zip_file_name" header="File Name" filter-field="zip_file_name" :show-filter-menu="false" style="min-width: 200px;">
            <template #body="slotProps">
              <template v-if="slotProps.data.uploaded_zip_details?.stored_zip_file?.stored_file?.original_filename">
                {{ slotProps.data.uploaded_zip_details.stored_zip_file.stored_file.original_filename }}
              </template>
            </template>
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search filename"
                @input="filterCallback()" />
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <router-link :to="{ name: 'admin_unos_finalized_offers_csv_batch_show', params: { id: slotProps.data.id } }"
                class="action btn btn-fill btn-outline-primary mx-2">
                Details
              </router-link>
            </template>
          </Column>
        </template>
      </PortalTable>
    </template>
  </CardSection>
</template>

<script lang="ts">
import { EP } from '@/src/endpoints';
import beApiClient from '@/src/be-api-client';
import CardSection from "@/src/components/shared/CardSection.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { FilterMatchMode } from '@primevue/core/api';
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import { paginationMixin } from "@/src/mixins/paginationMixin";
import { FinalizedOffersCSVBatch } from '@/src/models/unos/finalized-offers-csv-batch';
import { useTxcStore } from '@/src/stores/transplant_centers';

export default {
  name: 'FinalizedOfferCSVBatchesIndex',
  mixins: [savableStateMixin, paginationMixin],
  components: {
    CardSection,
    PortalTable,
    Column,
    InputText,
    MultiSelectFilter,
    VueDatePicker
  },
  setup() {
    const txcStore = useTxcStore();
    return { txcStore };
  },
  data() {
    return {
      filterValues: {
        transplant_center_id: { value: null, matchMode: FilterMatchMode.IN },
        batch_date: { value: null, matchMode: FilterMatchMode.EQUALS },
        status: { value: null, matchMode: FilterMatchMode.IN },
        created_at: { value: null, matchMode: FilterMatchMode.EQUALS },
        zip_file_name: { value: null, matchMode: FilterMatchMode.CONTAINS }
      },

    }
  },
  computed: {
    statusOptions() {
      return Object.entries(FinalizedOffersCSVBatch.BatchStatues).map(([code, { text }]) => ({ code, name: text }));
    }
  },
  methods: {
    async getRowData(reset = false) {
      try {
        this.setSavableStateProcessing();
        if (reset) this.pageNumber = 1;
        const response = await beApiClient.get(EP.admin.finalized_offers_csv_batches.index, {
          params: {
            ...this.searchParams,
            page: this.pageNumber,
            sort_by: this.currentSortField,
            sort_type: this.currentSortType
          }
        });
        
        /* TODO: Define a nicer method to parse records of a specific UI Model */
        this.processPagyResponse(response.data, (records: any[]) => {
          const parsedRecords: FinalizedOffersCSVBatch[] = [];
          records.forEach(r => parsedRecords.push(FinalizedOffersCSVBatch.buildFromAPI(r)));
          
          return parsedRecords;
        });
        this.setSavableStateIdle();
      } catch (error) {
        this.handleMajorError(error);
      }
    },
    onChange(event: any) {
      this.$refs.finalized_offers_csv_batches_index.resetFilters(event);
    }
  },
  async mounted() {
    try {
      this.setSavableStateLoading();
      await this.txcStore.loadTransplantCentres();
      this.setSavableStateIdle();
    } catch (error) {
      this.handleMajorError(error);
      return;
    }
    this.getRowData();
  }
}
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
  border-radius: 0.25rem;
  text-align: center;
  white-space: nowrap;
  text-transform: uppercase;
}
</style>
